import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import FormField, { PosInput } from '../components/pos/FormField';
import {
    listAdminReferences, createAdminReference, updateAdminReference,
    deleteAdminReference, reorderAdminReferences
} from '../services/integrationApi';

const TAB_IDS = [
    { id: 'vat_rate', labelKey: 'tabVat' },
    { id: 'unit', labelKey: 'tabUnit' },
    { id: 'payment_subject', labelKey: 'tabPaymentSubject' },
    { id: 'payment_method_54', labelKey: 'tabPaymentMethod' },
    { id: 'marking_type', labelKey: 'tabMarking' },
    { id: 'country', labelKey: 'tabCountry' },
    { id: 'okved', labelKey: 'tabOkved' }
];

export default function AdminReferencesPage() {
    const { t } = useTranslation(['adminReferences', 'common', 'nav', 'messages', 'errors']);
    const tabs = useMemo(
        () => TAB_IDS.map(item => ({ ...item, label: t(`adminReferences:${item.labelKey}`) })),
        [t]
    );
    const role = localStorage.getItem('role');
    const [tab, setTab] = useState('vat_rate');
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ code: '', name: '', shortName: '', description: '', sortOrder: 0, isActive: true });
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    useEffect(() => { if (role === 'Admin') load(); }, [tab, role]);

    async function load() {
        const data = await listAdminReferences(tab);
        setItems((Array.isArray(data) ? data : []).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
    }

    async function save() {
        try {
            if (editId) await updateAdminReference(editId, form);
            else await createAdminReference({ ...form, type: tab });
            setMsg(editId ? t('adminReferences:saved') : t('adminReferences:added'));
            setShowForm(false);
            load();
        } catch (e) {
            setErr(e.response?.data?.message || t('common:error'));
        }
    }

    async function deactivate(id, isSystem) {
        if (isSystem) { setErr(t('adminReferences:systemDeleteForbidden')); return; }
        try {
            await deleteAdminReference(id);
            setMsg(t('adminReferences:deactivated'));
            load();
        } catch (e) {
            setErr(e.response?.data?.message || t('common:error'));
        }
    }

    async function moveSort(idx, dir) {
        const next = [...items];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        const payload = next.map((r, i) => ({ id: r.id, sortOrder: i + 1 }));
        await reorderAdminReferences(payload);
        setItems(next.map((r, i) => ({ ...r, sortOrder: i + 1 })));
    }

    if (role !== 'Admin') {
        return (
            <OwnerPageLayout title={t('adminReferences:title')} className="pos-page pos-ui" maxWidth={1000}>
                <p>{t('adminReferences:adminOnly')}</p>
            </OwnerPageLayout>
        );
    }

    const activeTabLabel = tabs.find(x => x.id === tab)?.label || '';

    return (
        <OwnerPageLayout
            title={t('adminReferences:title')}
            subtitle={t('adminReferences:subtitle')}
            className="pos-page pos-ui"
            maxWidth={1000}
        >
                <Link to="/admin/export-templates" className="pos-btn pos-btn--ghost" style={{ textDecoration: 'none', marginBottom: 16, display: 'inline-block' }}>
                    {t('adminReferences:exportTemplatesLink')}
                </Link>
                {msg && <p style={{ color: '#15803d' }}>✓ {msg}</p>}
                {err && <p className="cashier-error">⚠️ {err}</p>}
                <div className="pos-tabs" style={{ flexWrap: 'wrap' }}>
                    {tabs.map(tabItem => (
                        <button key={tabItem.id} type="button" className={tab === tabItem.id ? 'active' : ''} onClick={() => setTab(tabItem.id)}>
                            {tabItem.label}
                        </button>
                    ))}
                </div>
                <button type="button" className="pos-btn pos-btn--primary" style={{ marginBottom: 16 }}
                    onClick={() => { setEditId(null); setForm({ code: '', name: '', shortName: '', sortOrder: items.length + 1, isActive: true }); setShowForm(true); }}>
                    {t('adminReferences:addBtn')}
                </button>
                <table className="pos-table">
                    <thead>
                        <tr><th></th><th>{t('adminReferences:colCode')}</th><th>{t('adminReferences:colName')}</th><th>{t('adminReferences:colShort')}</th><th>{t('adminReferences:colSystem')}</th><th>{t('adminReferences:colStatus')}</th><th></th></tr>
                    </thead>
                    <tbody>
                        {items.map((r, idx) => (
                            <tr key={r.id}>
                                <td>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '2px 6px' }} onClick={() => moveSort(idx, -1)} disabled={idx === 0}>↑</button>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '2px 6px' }} onClick={() => moveSort(idx, 1)} disabled={idx === items.length - 1}>↓</button>
                                </td>
                                <td>{r.code}</td>
                                <td>{r.name}</td>
                                <td>{r.shortName || '—'}</td>
                                <td>{r.isSystem ? t('adminReferences:systemYes') : t('adminReferences:systemNo')}</td>
                                <td>{r.isActive ? t('adminReferences:statusActive') : t('adminReferences:statusOff')}</td>
                                <td>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '6px 10px' }}
                                        title={t('common:edit')}
                                        onClick={() => { setEditId(r.id); setForm({ name: r.name, shortName: r.shortName, description: r.description, sortOrder: r.sortOrder, isActive: r.isActive }); setShowForm(true); }}>
                                        ✏️
                                    </button>
                                    {r.isSystem ? (
                                        <span title={t('adminReferences:systemLockTitle')} style={{ marginLeft: 4 }}>🔒</span>
                                    ) : (
                                        <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '6px 10px' }}
                                            onClick={() => deactivate(r.id, r.isSystem)}>🚫</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!items.length && <p style={{ color: 'var(--pos-muted)' }}>{t('adminReferences:empty')}</p>}
                {showForm && (
                    <div className="pos-modal-backdrop" onClick={() => setShowForm(false)}>
                        <div className="pos-modal" onClick={e => e.stopPropagation()}>
                            <h3>{editId ? t('common:edit') : t('adminReferences:addTitle', { tab: activeTabLabel })}</h3>
                            {!editId && (
                                <FormField label={t('adminReferences:fieldCode')}>
                                    <PosInput value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
                                </FormField>
                            )}
                            <FormField label={t('adminReferences:fieldName')}>
                                <PosInput value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                            </FormField>
                            <FormField label={t('adminReferences:fieldShort')}>
                                <PosInput value={form.shortName || ''} onChange={e => setForm({ ...form, shortName: e.target.value })} />
                            </FormField>
                            <FormField label={t('adminReferences:fieldDesc')}>
                                <PosInput value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
                            </FormField>
                            <FormField label={t('adminReferences:fieldOrder')}>
                                <PosInput type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })} />
                            </FormField>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                <button type="button" className="pos-btn pos-btn--primary" onClick={save}>{t('common:save')}</button>
                                <button type="button" className="pos-btn pos-btn--ghost" onClick={() => setShowForm(false)}>{t('common:cancel')}</button>
                            </div>
                        </div>
                    </div>
                )}
        </OwnerPageLayout>
    );
}
